<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import JabvoxCallButton from './jabvox/JabvoxCallButton.vue';
import JabvoxSmsButton from './jabvox/JabvoxSmsButton.vue';
import JabvoxEmailButton from './jabvox/JabvoxEmailButton.vue';
import JabvoxQuickButtons from './jabvox/JabvoxQuickButtons.vue';
import ConversationCallButton from './ConversationCallButton.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_participating: 'participating',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

// Conversación de grupo de WhatsApp: se identifica por additional_attributes que fija
// el backend al recibir el primer mensaje del grupo. Mostramos el nombre del grupo (no
// el del primer participante) y un distintivo.
const groupAttributes = computed(() => props.chat?.additional_attributes || {});
const isWhatsappGroup = computed(
  () => groupAttributes.value.is_whatsapp_group === true
);
const whatsappGroupName = computed(
  () => groupAttributes.value.group_name || ''
);

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(
  () => props.chat?.applied_sla?.id && !currentContact.value?.blocked
);

const isVoipEnabled = computed(() =>
  store.getters['accounts/isFeatureEnabledonAccount'](
    store.getters.getCurrentAccountId,
    FEATURE_FLAGS.JABVOX_VOIP
  )
);

const isCustomizationEnabled = computed(() =>
  store.getters['accounts/isFeatureEnabledonAccount'](
    store.getters.getCurrentAccountId,
    FEATURE_FLAGS.JABVOX_CUSTOMIZATION
  )
);

const contactPhone = computed(() => currentContact.value?.phone_number);
const contactEmail = computed(() => currentContact.value?.email);

const copyConversationId = async () => {
  try {
    await copyTextToClipboard(String(props.chat.id));
    useAlert(t('CONVERSATION.HEADER.COPY_ID_SUCCESS'));
  } catch (error) {
    // error
  }
};
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex flex-col gap-3 items-center justify-between flex-1 w-full min-w-0 xl:flex-row px-3 pt-3 pb-2 h-24 xl:h-12"
  >
    <div
      class="flex items-center justify-start w-full xl:w-auto max-w-full min-w-0 xl:flex-1"
    >
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="ltr:mr-2 rtl:ml-2"
      />
      <Avatar
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="32"
        :status="currentContact.availability_status"
        hide-offline-status
      />
      <div
        class="flex flex-col items-start min-w-0 ml-2 overflow-hidden rtl:ml-0 rtl:mr-2"
      >
        <div class="flex flex-row items-center max-w-full gap-1 p-0 m-0">
          <span
            v-if="isWhatsappGroup"
            v-tooltip="$t('CONVERSATION.WHATSAPP_GROUP')"
            class="flex items-center justify-center flex-shrink-0 w-4 h-4 rounded-full bg-n-teal-9/15 text-n-teal-11"
          >
            <fluent-icon icon="people-team" size="12" />
          </span>
          <span
            class="text-sm font-medium truncate leading-tight text-n-slate-12"
          >
            {{ isWhatsappGroup ? whatsappGroupName : currentContact.name }}
          </span>
          <span
            v-if="isWhatsappGroup"
            class="flex-shrink-0 rounded-full bg-n-teal-9/15 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-n-teal-11"
          >
            {{ $t('CONVERSATION.WHATSAPP_GROUP_BADGE') }}
          </span>
          <fluent-icon
            v-if="!isHMACVerified && !isWhatsappGroup"
            v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
            size="14"
            class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
            icon="warning"
          />
        </div>

        <div
          class="flex items-center gap-1 overflow-hidden text-xs conversation--header--actions text-n-slate-11 text-ellipsis whitespace-nowrap"
        >
          <button
            type="button"
            class="truncate text-label-small text-n-slate-11 hover:text-n-slate-12 !p-0 cucursor-pointer"
            @click="copyConversationId"
          >
            {{ `#${chat.id}` }}
          </button>
          <span v-if="hasMultipleInboxes">•</span>
          <InboxName v-if="hasMultipleInboxes" :inbox="inbox" class="!mx-0" />
          <span v-if="isSnoozed">•</span>
          <span v-if="isSnoozed" class="font-medium text-n-amber-10">
            {{ snoozedDisplayText }}
          </span>
        </div>
      </div>
    </div>
    <div
      class="flex flex-row items-center justify-start xl:justify-end flex-shrink-0 gap-2 w-full xl:w-auto header-actions-wrap"
    >
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <JabvoxCallButton
        v-if="isVoipEnabled && (contactPhone || currentContact.id)"
        :phone="contactPhone || ''"
        :contact-id="currentContact.id"
        :contact-name="currentContact.name"
      />
      <JabvoxSmsButton
        v-if="contactPhone && currentContact.id"
        :phone="contactPhone"
        :contact-id="currentContact.id"
      />
      <JabvoxEmailButton
        v-if="contactEmail && currentContact.id"
        :email="contactEmail"
        :contact-id="currentContact.id"
      />
      <JabvoxQuickButtons
        v-if="isCustomizationEnabled && currentContact.id"
        :contact="currentContact"
        size="sm"
      />
      <ConversationCallButton :inbox="inbox" :chat="currentChat" />
      <MoreActions :conversation-id="currentChat.id" />
    </div>
  </div>
</template>
